import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import { useInventoryQuery, useStrainCatalogQuery } from "../hooks/useInventoryQuery.js";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../utility/inventoryConstants.js";

const STATUS_COLORS = {
  growing: "bg-blue-100 text-blue-700",
  ready: "bg-green-100 text-green-700",
  depleted: "bg-gray-100 text-gray-500",
  contaminated: "bg-red-100 text-red-700",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function CatalogTab({ entries, search, setSearch }) {
  const filtered = entries.filter(
    (e) =>
      e.common_name.toLowerCase().includes(search.toLowerCase()) ||
      e.species.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div>
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search strains…"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cyan-500"
      />
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {filtered.length === 0 && (
          <p className="px-4 py-3 text-sm text-gray-400">No strains found.</p>
        )}
        {filtered.map((e) => (
          <div key={e.id} className="flex items-center justify-between px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">{e.common_name}</p>
              <p className="text-xs text-gray-500 italic">{e.species}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Inventory() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("stock");
  const [catalogSearch, setCatalogSearch] = useState("");

  const { data: items = [], isLoading: itemsLoading } = useInventoryQuery();
  const { data: catalog = [], isLoading: catalogLoading } = useStrainCatalogQuery();

  const lowStock = items.filter(
    (i) => i.low_stock_threshold != null && i.quantity <= i.low_stock_threshold
  );

  const grouped = items.reduce((acc, item) => {
    const cat = item.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const knownCats = new Set(CATEGORY_ORDER);
  const unknownCats = Object.keys(grouped).filter((c) => !knownCats.has(c));

  return (
    <LayoutWide title="Inventory">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-cyan-800">Inventory</h1>
          {tab === "stock" && (
            <button
              onClick={() => navigate("/inventory/new")}
              className="bg-cyan-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cyan-700 transition-colors"
            >
              + New item
            </button>
          )}
        </div>

        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {["stock", "catalog"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t
                  ? "border-cyan-600 text-cyan-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t === "stock" ? "My Stock" : "Strain Catalog"}
            </button>
          ))}
        </div>

        {tab === "catalog" && (
          catalogLoading
            ? <p className="text-sm text-gray-400">Loading…</p>
            : <CatalogTab entries={catalog} search={catalogSearch} setSearch={setCatalogSearch} />
        )}

        {tab === "stock" && (
          <>
            {itemsLoading && <p className="text-sm text-gray-400">Loading…</p>}

            {!itemsLoading && items.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <p className="text-lg mb-2">No inventory yet</p>
                <button
                  onClick={() => navigate("/inventory/new")}
                  className="text-cyan-600 hover:underline text-sm"
                >
                  Add your first item
                </button>
              </div>
            )}

            {lowStock.length > 0 && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-medium text-amber-800 mb-2">Low stock</p>
                <div className="space-y-1">
                  {lowStock.map((i) => (
                    <button
                      key={i.id}
                      onClick={() => navigate(`/inventory/${i.id}`)}
                      className="w-full text-left text-sm text-amber-700 hover:text-amber-900"
                    >
                      {i.name} — {i.quantity} {i.unit} remaining
                    </button>
                  ))}
                </div>
              </div>
            )}

            {[...CATEGORY_ORDER, ...unknownCats].map((cat) => {
              const group = grouped[cat];
              if (!group?.length) return null;
              const label = CATEGORY_LABELS[cat] ?? "Unknown";
              return (
                <div key={cat} className="mb-8">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
                    {label}
                  </h2>
                  <div className="space-y-2">
                    {group.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => navigate(`/inventory/${item.id}`)}
                        className="w-full text-left bg-white rounded-lg border border-gray-200 px-4 py-3 hover:border-cyan-300 hover:shadow-sm transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                            {item.species && (
                              <p className="text-xs text-gray-500 italic truncate">{item.species}</p>
                            )}
                            {item.generation && (
                              <p className="text-xs text-gray-400">{item.generation}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <StatusBadge status={item.status} />
                            <p className="text-xs text-gray-600">
                              {item.quantity} {item.unit}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </LayoutWide>
  );
}
